// Cart on every device — the saved cart. Signed-in sync is the ONLY mechanism (owner decision 21 Oct 2026:
// Change B, "Send this cart to another device", is removed — no button, no link, no QR code).
//
// Customer spec: "Cart on every device" v1.0 (R1–R17). Build plan §2 (S1–S12).
// The DBM side lives behind the App Proxy at /apps/consultant/api/extension/cart-sync; Shopify signs the
// query, and DBM acts only on the signed customer. This script is a CONVENIENCE: a wrong or missing answer
// may cost the buyer convenience, never money and never someone else's data (AC-IDN-132 exception).
//
// WHAT IT DOES
//   Save (R2–R5, R8): every cart write made through fetch to /cart/* is caught at ONE choke point (a fetch
//     wrapper). 2.5 s after the last write it reads /cart.js, keeps only the lines the buyer chose herself
//     (S4: no line with any `_` or `dbm.` property — vouchers, gifts, awards, gift-card details — no donation,
//     no gift product) and POSTs variant + quantity only when that list changed. On pagehide it sends what is
//     pending with fetch keepalive (sendBeacon cannot set the custom header DBM requires).
//   Load (R6, R7, R9–R11, R14): in full mode it asks DBM for the saved cart on EVERY page load, and again when
//     the tab becomes visible (at most once per 5 s, never while a save, a restore or a question is open), so
//     a cart saved on her phone reaches the laptop tab she switches back to. Shadow asks once per 10 minutes
//     per tab (it covers every consultant; a GET per page would multiply the load for a measurement).
//     Empty cart here → load it. Different cart here → ask once per saved revision. An award claimed in a cart
//     on another device → offer to release it. A cart already ordered → never load, offer to empty this one.
//     A revision this device saved is never loaded back (it would undo her newer change).
//   Restore: one /cart/add.js per line, in order (offer lines were saved last), so a refusal costs only that
//     line. Refusals are listed in plain English (R10). A whole-cart refusal (agreement not accepted,
//     view-only session) stops at once with ONE notice, and the device then never saves the empty cart over
//     the saved one, and does not try that revision again for 10 minutes. It never calls cart/clear and never
//     copies cart attributes. It ends with cart:refresh, so the voucher script's own load-restore runs AFTER it.
//   Conflict (R8): a save refused because another device saved first (409 stale_revision) acts like a load:
//     an empty cart here LOADS the other device's cart; it is never saved as an empty cart over it.
//   Sign-out (R12, S11): a cart stamped for one buyer (_dbm_cart_owner, a server-made opaque value) is never
//     saved to another buyer, and is emptied on the next page when nobody, or somebody else, is signed in.
//
// SWITCH (R17): window.__dbmCartSync.mode from shop metafield dbm.saved_cart_mode — off | shadow | pilot | on.
//   off: return at once; no wrapper, no listener, no call. shadow: saves only. pilot: full for customers tagged
//   dbm-cart-sync-pilot, shadow for the rest. on: full. The sign-out empty (R12) runs in pilot and on.
//
// FAIL CLOSED (S12): every path is wrapped; any exception, timeout or unexpected answer means "do nothing".
(function (win) {
  'use strict';

  // --- cart sync core (pure; extracted by tests/dbm-cart-sync.test.mjs) ---
  var ENDPOINT = '/apps/consultant/api/extension/cart-sync';
  var DONATION_VARIANT_ID = '42597741494318'; // Children's Hospital Trust donation (checkout-donation extension)
  var MAX_SAVE_LINES = 100;
  var MAX_QTY = 999;
  var DEBOUNCE_MS = 2500;
  var LOAD_TIMEOUT_MS = 1500;
  var POST_TIMEOUT_MS = 5000;
  var CHECK_TTL_MS = 10 * 60 * 1000; // shadow only; full mode checks on every page
  var VISIBLE_GAP_MS = 5000; // full mode: a tab returning to view checks again, at most once per 5 s
  var REF_LIFETIME_MS = 30 * 864e5;
  var RETRY_DELAYS_MS = [800, 1600, 3000];
  var MAX_RETRY_AFTER_MS = 3000;
  var OWNER_ATTR = '_dbm_cart_owner';
  var REV_ATTR = '_dbm_saved_cart_rev';
  var REF_ATTR = 'dbm.consultant_ref';
  var REF_COOKIE = 'dbm_skin_scan_ref';
  var REF_AT_COOKIE = 'dbm_skin_scan_ref_at';

  var COPY = {
    promptTitle: 'You have a saved cart from another device',
    promptBody: 'The cart on this device is different. Which cart do you want to use?',
    load: 'Load my saved cart',
    keep: 'Keep this cart',
    awardBody: 'An award is claimed in your cart on another device. To load your saved cart here, the award must be released first.',
    awardLoad: 'Release my award and load my saved cart',
    awardFailed: 'We could not release your award, so your saved cart was not loaded. This cart is unchanged.',
    orderedTitle: 'This cart was ordered on another device',
    orderedBody: 'Do you want to empty this cart, or keep it?',
    empty: 'Empty this cart',
    loadedTitle: 'We loaded your saved cart from your other device.',
    droppedTitle: 'Some products from your saved cart could not be added',
    droppedFoot: 'The other products are in your cart.',
    blockedTitle: 'Your saved cart could not be loaded',
    ok: 'OK',
    busy: 'The shop is busy right now. Please try again in a minute.',
    gone: 'One product is no longer on sale.',
    notAdded: 'One product could not be added.',
    notNow: 'One product could not be added right now.'
  };

  function has(obj, key) { return !!obj && Object.prototype.hasOwnProperty.call(obj, key); }

  // R17 — the mode this viewer actually gets. Anything unknown is off.
  function effectiveMode(mode, pilot) {
    if (mode === 'on' || mode === 'shadow') { return mode; }
    if (mode === 'pilot') { return pilot === true ? 'on' : 'shadow'; }
    return 'off';
  }

  // The switch gives SOMEONE the full feature (pilot or on). Used where the viewer's own mode is not known:
  // signed out, the sign-out empty (R12) must still run for a cart that full mode stamped.
  function fullFeatureLive(mode) { return mode === 'pilot' || mode === 'on'; }

  function digits(value) {
    if (value === null || value === undefined) { return ''; }
    var s = String(value).trim();
    var gid = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(s);
    if (gid) { return gid[1]; }
    return /^\d{1,20}$/.test(s) ? s : '';
  }

  function clampQty(value) {
    var n = Math.floor(Number(value));
    if (!(n >= 1)) { return 0; }
    return n > MAX_QTY ? MAX_QTY : n;
  }

  // Property keys of a /cart.js line, object or array form.
  function propertyKeys(line) {
    var p = line && line.properties;
    var keys = [];
    if (!p) { return keys; }
    if (Array.isArray(p)) {
      for (var i = 0; i < p.length; i++) {
        var entry = p[i];
        var k = entry && (entry.key || entry.name);
        if (k && entry.value !== null && entry.value !== undefined) { keys.push(String(k)); }
      }
      return keys;
    }
    if (typeof p === 'object') {
      for (var key in p) {
        if (has(p, key) && p[key] !== null && p[key] !== undefined) { keys.push(String(key)); }
      }
    }
    return keys;
  }

  // S4 / R3 / R4 — a line the buyer did not choose herself is never saved. Marker-based only, never price-based.
  //   any `_` property ... _dbm_award*, _dbm_gwp*, _dbm_voucher_id, __shopify_* gift-card details, any private line
  //   any `dbm.` property  the theme's own namespace
  //   the donation variant
  //   a gift product (dbm.fn_gift, from Liquid / the line markers) or a variant of the GWP gift collection
  function isExcludedLine(line, flags) {
    if (!line) { return true; }
    var vid = digits(line.variant_id !== undefined ? line.variant_id : line.id);
    if (!vid) { return true; }
    if (vid === DONATION_VARIANT_ID) { return true; }
    var keys = propertyKeys(line);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].charAt(0) === '_' || keys[i].indexOf('dbm.') === 0) { return true; }
    }
    var f = flags || {};
    if (has(f.giftVariants, vid)) { return true; }
    if (line.key && has(f.giftKeys, String(line.key))) { return true; }
    return false;
  }

  function isOfferLine(line, flags) {
    var f = flags || {};
    var vid = digits(line.variant_id !== undefined ? line.variant_id : line.id);
    return has(f.offerVariants, vid) || (!!line.key && has(f.offerKeys, String(line.key)));
  }

  // The buyer's own lines: variant + quantity, duplicates merged, ordinary lines first and offer lines LAST
  // (an offer's allowance depends on the rest of the cart, so on restore it must be added after them).
  function ownLines(cart, flags, cap) {
    var items = (cart && Array.isArray(cart.items)) ? cart.items : [];
    var ordinary = [];
    var offers = [];
    var index = {};
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (isExcludedLine(line, flags)) { continue; }
      var vid = digits(line.variant_id !== undefined ? line.variant_id : line.id);
      var qty = clampQty(line.quantity);
      if (!qty) { continue; }
      if (has(index, vid)) {
        index[vid].quantity = Math.min(MAX_QTY, index[vid].quantity + qty);
        continue;
      }
      var entry = { variant_id: Number(vid), quantity: qty };
      index[vid] = entry;
      (isOfferLine(line, flags) ? offers : ordinary).push(entry);
    }
    return ordinary.concat(offers).slice(0, cap || MAX_SAVE_LINES);
  }

  // Order-free content identity of a line list.
  function linesHash(lines) {
    var parts = [];
    for (var i = 0; i < (lines || []).length; i++) {
      parts.push(String(lines[i].variant_id) + ':' + String(lines[i].quantity));
    }
    parts.sort();
    return parts.join(',');
  }

  // A saved revision's lines from DBM ({variantId, quantity}) in the save shape, invalid ones dropped.
  function normalizeSavedLines(lines) {
    var out = [];
    var seen = {};
    for (var i = 0; i < (Array.isArray(lines) ? lines : []).length; i++) {
      var l = lines[i] || {};
      var vid = digits(l.variantId !== undefined ? l.variantId : l.variant_id);
      var qty = clampQty(l.quantity);
      if (!vid || !qty || has(seen, vid)) { continue; }
      seen[vid] = true;
      out.push({ variant_id: Number(vid), quantity: qty });
    }
    return out.slice(0, MAX_SAVE_LINES);
  }

  // The load decision. Pure. input = { mode, resp, own, cartOwner, cartRev, deviceId, shown(key), emptiedOwner }
  //   foreign       the cart here belongs to another buyer (S11)
  //   restore       load the saved cart (R6)
  //   prompt        R7 — ask once per saved revision
  //   award_prompt  R11
  //   ordered       R14
  //   stamp_rev     same content already here: just mark this cart as that revision
  //   none
  function decideLoad(input) {
    var resp = input.resp;
    if (!resp || resp.signedIn !== true) { return { action: 'none', reason: 'signed_out' }; }
    if (input.cartOwner && resp.owner && input.cartOwner !== resp.owner) { return { action: 'foreign', reason: 'owner_mismatch' }; }
    if (input.mode !== 'on') { return { action: 'none', reason: 'mode' }; }
    var rev = Number(resp.rev) || 0;
    if (rev <= 0) { return { action: 'none', reason: 'no_saved_cart' }; }
    var saved = normalizeSavedLines(resp.lines);
    var own = input.own || [];
    var shown = input.shown || function () { return false; };
    if (resp.ordered === true) {
      // R14 — never loaded again by itself. Tell a device still holding that cart, once.
      var sameCart = String(input.cartRev || '') === String(rev) || (own.length > 0 && linesHash(own) === linesHash(saved));
      if (own.length && sameCart && !shown('ordered:' + rev)) { return { action: 'ordered', rev: rev }; }
      return { action: 'none', reason: 'ordered' };
    }
    var emptiedHere = !!input.emptiedOwner && input.emptiedOwner === resp.owner;
    if (resp.deviceId && resp.deviceId === input.deviceId && !emptiedHere) {
      return { action: 'none', reason: 'own_device_revision' };
    }
    if (!saved.length) { return { action: 'none', reason: 'saved_empty' }; }
    if (own.length && linesHash(own) === linesHash(saved)) {
      return String(input.cartRev || '') === String(rev) ? { action: 'none', reason: 'equal' } : { action: 'stamp_rev', rev: rev };
    }
    if (resp.awardClaimElsewhere === true) {
      return shown('award:' + rev) ? { action: 'none', reason: 'award_prompted' } : { action: 'award_prompt', rev: rev };
    }
    if (!own.length) { return { action: 'restore', rev: rev }; }
    return shown('prompt:' + rev) ? { action: 'none', reason: 'prompted' } : { action: 'prompt', rev: rev };
  }

  // Build plan step 3 (shadow) — what full mode WOULD have done, as one '<action>:<reason>' value for the
  // shadow_decision event. Pure. The set is closed (DBM drops anything else): '' means "nothing to report"
  // (signed out, or the mode guard, which a dry run never reaches because it forces mode 'on').
  var SHADOW_REASON = {
    restore: 'empty_cart', prompt: 'different_cart', award_prompt: 'award_elsewhere',
    ordered: 'cart_still_here', stamp_rev: 'same_lines', foreign: 'owner_mismatch'
  };
  var SHADOW_NONE = ['no_saved_cart', 'ordered', 'own_device_revision', 'saved_empty', 'equal', 'award_prompted', 'prompted'];
  function shadowDecisionValue(d) {
    if (!d || typeof d.action !== 'string') { return ''; }
    if (d.action === 'none') { return SHADOW_NONE.indexOf(d.reason) !== -1 ? 'none:' + d.reason : ''; }
    return has(SHADOW_REASON, d.action) ? d.action + ':' + SHADOW_REASON[d.action] : '';
  }

  // Whether a save may be sent. Pure. trigger: change | load | keep | retry.
  function saveGate(input) {
    if (!input.identity) { return { send: false, reason: 'signed_out' }; }
    if (!input.state || !input.state.owner) { return { send: false, reason: 'no_state' }; }
    if (input.cartOwner && input.cartOwner !== input.state.owner) { return { send: false, reason: 'foreign_owner' }; }
    if (input.promptPending) { return { send: false, reason: 'prompt_pending' }; }
    if (input.backoffUntil && input.now < input.backoffUntil) { return { send: false, reason: 'backoff' }; }
    var own = input.own || [];
    var last = input.lastSent;
    var sameCart = !!last && last.id === input.identity && last.token === input.token;
    if (input.trigger === 'keep') { return own.length ? { send: true, reason: 'keep' } : { send: false, reason: 'empty' }; }
    if (sameCart && last.hash === linesHash(own)) { return { send: false, reason: 'unchanged' }; }
    if (!own.length) {
      if (input.blockedRev !== null && input.blockedRev !== undefined && String(input.blockedRev) === String(input.state.rev)) {
        return { send: false, reason: 'blocked_empty' };
      }
      // An empty NEW cart is a finished checkout or an expired cart, not her removing everything.
      if (!sameCart) { return { send: false, reason: 'empty_new_cart' }; }
    }
    // R2 — a cart filled before she signed in is saved at her first CHANGE, never just because a page loaded.
    if (input.trigger === 'load' && !sameCart) { return { send: false, reason: 'not_saved_here_yet' }; }
    return { send: true, reason: input.trigger || 'change' };
  }

  function cleanRef(value) {
    var raw = value === null || value === undefined ? '' : String(value).trim();
    return /^[0-9]{3,12}$/.test(raw) ? raw : '';
  }

  // R5 — the referral that travels: the device's parked customer link, for what is left of its 30 days,
  // never the buyer's own number.
  function referralFor(parked, selfRef, now) {
    var ref = cleanRef(parked && parked.ref);
    if (!ref || ref === cleanRef(selfRef)) { return null; }
    var at = Number(parked.at);
    if (!(at > 0)) { return null; }
    var exp = at + REF_LIFETIME_MS;
    if (exp <= now) { return null; }
    return { cust_no: ref, expires_at: new Date(exp).toISOString() };
  }

  // R5 — put the saved referral on this device only when it has none, or an older one; never her own number.
  function shouldRestampReferral(saved, device, selfRef, now) {
    if (!saved) { return false; }
    var ref = cleanRef(saved.custNo);
    var exp = Date.parse(saved.expiresAt);
    if (!ref || ref === cleanRef(selfRef) || !(exp > now)) { return false; }
    var devRef = cleanRef(device && device.ref);
    if (!devRef) { return true; }
    if (devRef === ref) { return false; }
    return (Number(device.at) || 0) < exp - REF_LIFETIME_MS;
  }

  // R10 — the reason Shopify (or a cart Function) gave, never the bare "Cart Error".
  function refusalText(status, data) {
    var candidates = [
      data && typeof data.description === 'string' ? data.description : '',
      data && typeof data.message === 'string' ? data.message : ''
    ];
    for (var i = 0; i < candidates.length; i++) {
      var t = candidates[i].trim();
      if (t && t.toLowerCase() !== 'cart error') { return t; }
    }
    if (status === 404) { return COPY.gone; }
    if (status === 422) { return COPY.notAdded; }
    return COPY.notNow;
  }

  // A refusal that will refuse EVERY line: the agreement gate and the view-only session (both target $.cart).
  function isWholeCartRefusal(text) {
    return /independent consultant agreement/i.test(text || '') || /view-only session/i.test(text || '');
  }

  function isCartWrite(url, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST') { return false; }
    var path = String(url || '').split('#')[0].split('?')[0];
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
  }

  function retryDelayMs(retryAfter, attempt) {
    var s = Number(retryAfter);
    if (retryAfter !== null && retryAfter !== undefined && retryAfter !== '' && isFinite(s) && s >= 0) {
      return Math.min(s * 1000, MAX_RETRY_AFTER_MS);
    }
    return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  }

  var core = {
    ENDPOINT: ENDPOINT, DONATION_VARIANT_ID: DONATION_VARIANT_ID, MAX_SAVE_LINES: MAX_SAVE_LINES,
    DEBOUNCE_MS: DEBOUNCE_MS, LOAD_TIMEOUT_MS: LOAD_TIMEOUT_MS, CHECK_TTL_MS: CHECK_TTL_MS,
    VISIBLE_GAP_MS: VISIBLE_GAP_MS, REF_LIFETIME_MS: REF_LIFETIME_MS, COPY: COPY,
    effectiveMode: effectiveMode, fullFeatureLive: fullFeatureLive, isExcludedLine: isExcludedLine, ownLines: ownLines,
    linesHash: linesHash, normalizeSavedLines: normalizeSavedLines, decideLoad: decideLoad, saveGate: saveGate,
    shadowDecisionValue: shadowDecisionValue,
    referralFor: referralFor, shouldRestampReferral: shouldRestampReferral, refusalText: refusalText,
    isWholeCartRefusal: isWholeCartRefusal,
    isCartWrite: isCartWrite, retryDelayMs: retryDelayMs, cleanRef: cleanRef
  };
  // --- end cart sync core ---

  // ---------------------------------------------------------------------------------------------------
  // Runtime. Everything the browser provides comes in through env, so the tests drive the shipped code.
  // ---------------------------------------------------------------------------------------------------
  function create(env) {
    var cfg = env.cfg || {};
    var mode = effectiveMode(cfg.mode, cfg.pilot);
    var identity = env.identity ? String(env.identity) : '';
    var saveOn = !!identity && (mode === 'shadow' || mode === 'on');
    var ui = env.ui;
    var rawFetch = env.fetch;
    var hold = env.hold || null;
    var restoring = false;
    var disabled = false;
    var promptPending = false;
    var timer = null;
    var pendingTrigger = null;
    var latestCart = null;
    var latestFresh = false;
    var reloading = false;
    var inflight = null; // the one save whose answer this page is waiting for
    var queued = null; // the save asked for while it was out (coalesced to one)
    var queuedP = null;
    var checking = false; // a load check is out
    var lastCheckAt = 0; // when this page last asked DBM for the saved cart

    var K = {
      device: 'dbm.cartSync.device', seq: 'dbm.cartSync.seq', state: 'dbm.cartSync.state',
      sent: 'dbm.cartSync.sent', shown: 'dbm.cartSync.shown', blocked: 'dbm.cartSync.blocked',
      emptied: 'dbm.cartSync.emptied', fullOwner: 'dbm.cartSync.fullOwner', backoff: 'dbm.cartSync.backoff',
      refSeen: 'dbm.cartSync.refSeen', check: 'dbm.cartSync.check', notice: 'dbm.cartSync.notice',
      pending: 'dbm.cartSync.pending', shadow: 'dbm.cartSync.shadowDecision'
    };
    var shadowReported = false; // at most one shadow_decision per page view

    function now() { return env.now(); }
    function noop() {}

    function readJson(store, key) {
      try { var v = store.getItem(key); return v ? JSON.parse(v) : null; } catch (e) { return null; }
    }
    function writeJson(store, key, value) {
      try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked */ }
    }
    function remove(store, key) { try { store.removeItem(key); } catch (e) {} }
    function getRaw(store, key) { try { return store.getItem(key); } catch (e) { return null; } }

    function mine(rec) { return rec && rec.id === identity ? rec : null; }

    function deviceId() {
      var id = getRaw(env.local, K.device);
      if (id && /^[A-Za-z0-9_-]{8,64}$/.test(id)) { return id; }
      var alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
      var bytes = env.randomBytes(22);
      id = '';
      for (var i = 0; i < bytes.length; i++) { id += alphabet.charAt(bytes[i] % 64); }
      try { env.local.setItem(K.device, id); } catch (e) {}
      return id;
    }

    function nextSeq() {
      var n = parseInt(getRaw(env.local, K.seq) || '0', 10);
      n = (n >= 0 ? n : 0) + 1;
      try { env.local.setItem(K.seq, String(n)); } catch (e) {}
      return n;
    }

    function state() { return mine(readJson(env.local, K.state)); }
    function setState(owner, rev) { writeJson(env.local, K.state, { id: identity, owner: owner, rev: Number(rev) || 0 }); }
    function lastSent() { return mine(readJson(env.local, K.sent)); }

    // A save sent whose answer this page has not seen yet: in flight, or a pagehide keepalive whose answer died
    // with the page (the cart page reloads right after /cart/change.js). DBM's success revision is always
    // if_rev + 1, so the next save goes out on that predicted revision instead of the stale one. A wrong guess
    // costs one 409, exactly what a stale revision cost before. Cleared by the save's own answer or by a GET.
    function pending() { return mine(readJson(env.local, K.pending)); }
    function setPending(token, lines, rev, seq) {
      writeJson(env.local, K.pending, { id: identity, token: token, hash: linesHash(lines), rev: rev, seq: seq });
    }
    function clearPending(seq) {
      var p = pending();
      if (p && (seq === undefined || p.seq === seq)) { remove(env.local, K.pending); }
    }
    function setSent(token, lines) { writeJson(env.local, K.sent, { id: identity, token: token, hash: linesHash(lines) }); }

    function shown(key) {
      var rec = mine(readJson(env.local, K.shown));
      return !!rec && Array.isArray(rec.keys) && rec.keys.indexOf(key) !== -1;
    }
    function markShown(key) {
      var rec = mine(readJson(env.local, K.shown)) || { id: identity, keys: [] };
      if (rec.keys.indexOf(key) === -1) { rec.keys.push(key); }
      rec.keys = rec.keys.slice(-30);
      writeJson(env.local, K.shown, rec);
    }

    function blockedRev() { var b = mine(readJson(env.local, K.blocked)); return b ? b.rev : null; }
    // This revision met a whole-cart refusal less than 10 minutes ago. Full mode checks every page, and the
    // refusal (agreement not accepted, view-only session) would only repeat: wasted adds, notice suppressed.
    function blockedRecently(rev) {
      var b = mine(readJson(env.local, K.blocked));
      if (!b || String(b.rev) !== String(rev)) { return false; }
      var at = Number(b.at);
      if (!(at > 0)) { return true; } // written before the time stamp existed: treat as just now
      var age = now() - at;
      return age >= 0 && age < CHECK_TTL_MS;
    }

    function bareToken(token) { return token ? String(token).split('?')[0] : null; }

    function attr(cart, name) {
      var a = cart && cart.attributes;
      var v = a && has(a, name) ? a[name] : '';
      return v === null || v === undefined ? '' : String(v);
    }

    // S4 facts the theme knows: Liquid flags for the lines rendered at page load, the line markers the cart
    // sections render (they survive a drawer re-render), and the GWP gift collection.
    function flags() {
      var f = { giftVariants: {}, giftKeys: {}, offerVariants: {}, offerKeys: {} };
      try {
        var lf = cfg.lineFlags || {};
        var k;
        for (k in (lf.gift || {})) { if (has(lf.gift, k)) { f.giftVariants[String(k)] = 1; } }
        for (k in (lf.offer || {})) { if (has(lf.offer, k)) { f.offerVariants[String(k)] = 1; } }
        var map = env.giftMap ? env.giftMap() : null;
        for (k in (map || {})) { if (has(map, k) && digits(map[k])) { f.giftVariants[digits(map[k])] = 1; } }
        var markers = env.lineMarkers ? env.lineMarkers() : [];
        for (var i = 0; i < markers.length; i++) {
          var m = markers[i];
          if (!m || !m.key) { continue; }
          if (m.gift) { f.giftKeys[m.key] = 1; }
          if (m.offer) { f.offerKeys[m.key] = 1; }
        }
      } catch (e) { /* fewer facts: the property rules still hold */ }
      return f;
    }

    function withTimeout(promise, ms, controller) {
      return new Promise(function (resolve, reject) {
        var t = env.setTimeout(function () {
          try { if (controller) { controller.abort(); } } catch (e) {}
          reject(new Error('timeout'));
        }, ms);
        promise.then(function (v) { env.clearTimeout(t); resolve(v); }, function (e) { env.clearTimeout(t); reject(e); });
      });
    }

    function call(url, options, ms) {
      var controller = env.AbortController ? new env.AbortController() : null;
      var opts = options || {};
      if (controller) { opts.signal = controller.signal; }
      return withTimeout(Promise.resolve().then(function () { return rawFetch(url, opts); }), ms, controller);
    }

    function cartRoot() { return env.root || '/'; }

    function readCart() {
      return call(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }, LOAD_TIMEOUT_MS)
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          if (cart && Array.isArray(cart.items)) { latestCart = cart; latestFresh = true; return cart; }
          return null;
        })
        .catch(function () { return null; });
    }

    function cartPost(path, body) {
      return call(cartRoot() + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }, 15000);
    }

    function dbmPost(path, body, keepalive) {
      var opts = {
        method: 'POST',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Dbm-Cart-Sync': '1' },
        body: JSON.stringify(body)
      };
      if (keepalive) { opts.keepalive = true; }
      return call(ENDPOINT + path, opts, POST_TIMEOUT_MS);
    }

    function readBody(r) {
      if (!r || typeof r.json !== 'function') { return Promise.resolve(null); }
      return r.json().catch(function () { return null; });
    }

    // GET the saved cart. null on anything but a 200 with a body (204 no-op, refusals, timeouts: do nothing).
    function getSaved(cartToken) {
      var url = ENDPOINT + '?expected_customer=' + encodeURIComponent(identity) +
        '&cart_token=' + encodeURIComponent(cartToken || '');
      return call(url, {
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer',
        headers: { Accept: 'application/json' }
      }, LOAD_TIMEOUT_MS).then(function (r) {
        if (!r || r.status !== 200) { return null; }
        return readBody(r);
      }).then(function (resp) {
        if (!resp || typeof resp !== 'object') { return null; }
        if (resp.signedIn === true && typeof resp.owner === 'string' && resp.owner) {
          setState(resp.owner, resp.rev);
          if (!inflight) { clearPending(); } // the server's word replaces any guess
        }
        return resp;
      }).catch(function () { return null; });
    }

    // Fire-and-forget. Every event is full-mode only, except shadow_decision, which is shadow-only.
    function sendEvent(name, value) {
      var allowed = name === 'shadow_decision' ? mode === 'shadow' : mode === 'on';
      if (!allowed || !identity) { return; }
      var body = { expected_customer: identity, name: name, value: value };
      if (name === 'shadow_decision') { body.audience = cfg.consultantRef ? 'consultant' : 'customer'; }
      try { dbmPost('/events', body).catch(noop); } catch (e) {}
    }

    // Build plan step 3 — in shadow, run the full-mode load decision as a DRY RUN and report what it would have
    // done. It READS the markers full mode uses (shown, emptied, device id) and writes none of them; it never
    // touches the cart or the page. Its only write is its own session key: once per page session per
    // (buyer, decision), and never more than one event per page view.
    function reportShadowDecision(resp, cart, own, cartOwner, emptied) {
      try {
        if (mode !== 'shadow' || !identity || shadowReported) { return; }
        var d = decideLoad({
          mode: 'on', resp: resp, own: own, cartOwner: cartOwner, cartRev: attr(cart, REV_ATTR),
          deviceId: getRaw(env.local, K.device) || '', shown: shown, emptiedOwner: emptied && emptied.owner
        });
        var value = shadowDecisionValue(d);
        if (!value) { return; }
        var rec = readJson(env.session, K.shadow);
        var keys = rec && rec.id === identity && Array.isArray(rec.keys) ? rec.keys : [];
        if (keys.indexOf(value) !== -1) { return; }
        shadowReported = true;
        keys.push(value);
        writeJson(env.session, K.shadow, { id: identity, keys: keys.slice(-20) });
        sendEvent('shadow_decision', value);
      } catch (e) { /* measurement only: never in the buyer's way */ }
    }

    function ensureHold(ms) {
      if (hold && !hold.isDone()) { hold.extend(ms); return; }
      hold = env.lock && env.lock.hold ? env.lock.hold(ms) : null;
    }
    function releaseHold() { if (hold) { try { hold.release(); } catch (e) {} } }

    function announce(notice) {
      if (env.onCartPage && env.onCartPage()) {
        // The cart page is server-rendered: show the notice after the reload.
        if (notice) { writeJson(env.session, K.notice, notice); }
        reloading = true;
        env.reload();
        return;
      }
      if (notice) { ui.notice(notice); }
      env.dispatch('cart:refresh');
    }

    function stampAttributes(attributes) {
      return cartPost('cart/update.js', { attributes: attributes }).then(function (r) { return !!(r && r.ok); }).catch(function () { return false; });
    }

    // R12 / S11 / R14 — empty the cart on this device. Every line to 0 by key; never cart/clear.
    function emptyCart(cart) {
      var updates = {};
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) { if (items[i] && items[i].key) { updates[items[i].key] = 0; } }
      var body = { updates: updates, attributes: {} };
      body.attributes[OWNER_ATTR] = '';
      body.attributes[REV_ATTR] = '';
      return cartPost('cart/update.js', body).then(function (r) { return !!(r && r.ok); }).catch(function () { return false; });
    }

    // --- referral (R5) ---
    function readCookie(name) {
      try {
        var parts = ('; ' + env.getCookie()).split('; ' + name + '=');
        if (parts.length < 2) { return null; }
        return decodeURIComponent(parts.pop().split(';').shift());
      } catch (e) { return null; }
    }

    function deviceReferral() {
      var ref = cleanRef(env.referralValue ? env.referralValue() : '') || cleanRef(readCookie(REF_COOKIE));
      if (!ref) { return { ref: '', at: 0 }; }
      var at = Number(readCookie(REF_AT_COOKIE));
      if (!(at > 0)) {
        // Parked before the time stamp existed: count from the first time this script saw it.
        var seen = readJson(env.local, K.refSeen);
        if (seen && seen.ref === ref && Number(seen.at) > 0) { at = Number(seen.at); }
        else { at = now(); writeJson(env.local, K.refSeen, { ref: ref, at: at }); }
      }
      return { ref: ref, at: at };
    }

    function parkReferral(ref, expiresAt) {
      var exp = Date.parse(expiresAt);
      try {
        var expires = new Date(exp).toUTCString();
        env.setCookie(REF_COOKIE + '=' + encodeURIComponent(ref) + '; path=/; expires=' + expires + '; SameSite=Lax');
        env.setCookie(REF_AT_COOKIE + '=' + String(exp - REF_LIFETIME_MS) + '; path=/; expires=' + expires + '; SameSite=Lax');
      } catch (e) {}
      try { env.session.setItem(REF_COOKIE, ref); } catch (e) {}
      if (env.setReferralValue) { env.setReferralValue(ref); }
    }

    // --- restore ---
    function addLine(line, attempt) {
      ensureHold(20000);
      return cartPost('cart/add.js', { items: [{ id: Number(line.variant_id), quantity: line.quantity }] })
        .then(function (r) {
          if (r && r.ok) { return { ok: true }; }
          var status = r ? r.status : 0;
          if (status === 429 && attempt < RETRY_DELAYS_MS.length) {
            var ra = r && r.headers && r.headers.get ? r.headers.get('Retry-After') : null;
            return env.delay(retryDelayMs(ra, attempt)).then(function () { return addLine(line, attempt + 1); });
          }
          if (status === 429) { return { ok: false, busy: true }; }
          return readBody(r).then(function (data) { return { ok: false, text: refusalText(status, data) }; });
        }, function () { return { ok: false, text: COPY.notNow }; });
    }

    // One /cart/add.js per line, in order. Returns { added, refused: [text], wholeCart: text|null, busy }.
    function addLines(lines, cap) {
      var list = lines.slice(0, cap);
      var out = { added: 0, refused: [], wholeCart: null, busy: false };
      var chain = Promise.resolve();
      list.forEach(function (line) {
        chain = chain.then(function () {
          if (out.wholeCart || out.busy) { return null; }
          return addLine(line, 0).then(function (res) {
            if (res.ok) { out.added++; return; }
            if (res.busy) { out.busy = true; out.refused.push(COPY.busy); return; }
            // A refusal that will refuse every line — stop at once (and the same reason twice before anything
            // landed is the same thing in a wording we do not know yet).
            if (isWholeCartRefusal(res.text) || (out.added === 0 && out.refused.length === 1 && out.refused[0] === res.text)) {
              out.wholeCart = res.text;
              return;
            }
            out.refused.push(res.text);
          });
        });
      });
      return chain.then(function () { return out; });
    }

    function droppedNotice(title, refused) {
      var items = [];
      for (var i = 0; i < refused.length; i++) { if (items.indexOf(refused[i]) === -1) { items.push(refused[i]); } }
      return { title: title, items: items, foot: COPY.droppedFoot };
    }

    // Load a saved revision into this cart. replaceOwn: R7 "Load my saved cart" replaces her own lines (carts are
    // never added together); the lines she did not choose (vouchers, gifts) are left for their own scripts.
    function restoreSaved(resp, opts) {
      var rev = Number(resp.rev) || 0;
      restoring = true;
      ensureHold(20000);
      return readCart().then(function (cart) {
        if (!cart) { throw new Error('no_cart'); }
        if (!opts.replaceOwn) { return cart; }
        var f = flags();
        var updates = {};
        var any = false;
        cart.items.forEach(function (line) {
          if (!isExcludedLine(line, f) && line.key) { updates[line.key] = 0; any = true; }
        });
        if (!any) { return cart; }
        return cartPost('cart/update.js', { updates: updates }).then(function (r) {
          if (!r || !r.ok) { throw new Error('replace_failed'); }
          return cart;
        });
      }).then(function () {
        return addLines(normalizeSavedLines(resp.lines), MAX_SAVE_LINES);
      }).then(function (res) {
        if (res.wholeCart) {
          // Nothing can be added: never save the resulting empty cart over the saved one.
          writeJson(env.local, K.blocked, { id: identity, rev: rev, at: now() });
          sendEvent('restore', 'error');
          var notice = null;
          if (!shown('blocked:' + rev)) {
            markShown('blocked:' + rev);
            notice = { title: COPY.blockedTitle, body: res.wholeCart };
          }
          return { notice: notice, changed: false };
        }
        remove(env.local, K.blocked);
        // She has had this revision: lines a refusal dropped must not come back as a "different cart" question
        // on the next page (full mode checks every page).
        markShown('prompt:' + rev);
        var attributes = {};
        attributes[OWNER_ATTR] = resp.owner;
        if (res.added > 0) { attributes[REV_ATTR] = String(rev); }
        var device = deviceReferral();
        if (shouldRestampReferral(resp.referral, device, cfg.selfRef, now())) {
          parkReferral(cleanRef(resp.referral.custNo), resp.referral.expiresAt);
          attributes[REF_ATTR] = cleanRef(resp.referral.custNo);
        }
        return stampAttributes(attributes).then(function () {
          try { env.local.setItem(K.fullOwner, resp.owner); } catch (e) {}
          remove(env.local, K.emptied);
          return readCart();
        }).then(function (fresh) {
          if (fresh) { setSent(bareToken(fresh.token), ownLines(fresh, flags())); }
          if (res.refused.length) { sendEvent('restore', 'lines_dropped'); }
          else if (opts.auto) { sendEvent('restore', 'auto'); }
          var notice = res.refused.length
            ? droppedNotice(COPY.droppedTitle, res.refused)
            : { title: COPY.loadedTitle };
          return { notice: notice, changed: res.added > 0 };
        });
      }).catch(function () {
        sendEvent('restore', 'error');
        return { notice: null, changed: false };
      }).then(function (outcome) {
        restoring = false;
        releaseHold();
        if (outcome.changed) { announce(outcome.notice); }
        else if (outcome.notice) { ui.notice(outcome.notice); }
        return outcome;
      });
    }

    // --- load (full mode: every page and every return to the tab; shadow: once per 10 minutes per buyer) ---
    function checkDue() {
      if (mode === 'on') { return true; }
      var c = readJson(env.session, K.check);
      if (!c || c.id !== identity) { return true; }
      var age = now() - Number(c.at);
      return !(age >= 0 && age < CHECK_TTL_MS);
    }
    function markChecked() { writeJson(env.session, K.check, { id: identity, at: now() }); }

    function act(resp, cart, depth) {
      var f = flags();
      var own = ownLines(cart, f);
      var cartOwner = attr(cart, OWNER_ATTR);
      var emptied = readJson(env.local, K.emptied);
      // Shadow: nothing visible ever happens here — only the dry run is reported.
      if (mode === 'shadow') { reportShadowDecision(resp, cart, own, cartOwner, emptied); return null; }
      var d = decideLoad({
        mode: mode, resp: resp, own: own, cartOwner: cartOwner, cartRev: attr(cart, REV_ATTR),
        deviceId: deviceId(), shown: shown, emptiedOwner: emptied && emptied.owner
      });
      var rev = Number(resp.rev) || 0;
      switch (d.action) {
        case 'foreign':
          if (mode !== 'on' || depth > 0) { return null; } // shadow: the save gate refuses this cart; nothing visible
          return emptyCart(cart).then(function (ok) {
            if (!ok) { return null; }
            return readCart().then(function (fresh) {
              if (!fresh) { return null; }
              return act(resp, fresh, depth + 1);
            }).then(function (r) {
              // A restore announces itself; otherwise the emptied cart still has to be repainted.
              if (!(r && r.changed)) { announce(null); }
              return r;
            });
          });
        case 'restore':
          if (blockedRecently(rev)) { return null; }
          return restoreSaved(resp, { replaceOwn: false, auto: true });
        case 'prompt':
          markShown('prompt:' + rev);
          sendEvent('restore', 'prompt');
          promptPending = true;
          ui.prompt({
            title: COPY.promptTitle,
            body: COPY.promptBody,
            buttons: [{ id: 'load', label: COPY.load, primary: true }, { id: 'keep', label: COPY.keep }]
          }).then(function (choice) {
            promptPending = false;
            if (choice === 'load') { sendEvent('prompt_choice', 'load'); return restoreSaved(resp, { replaceOwn: true }); }
            if (choice === 'keep') { sendEvent('prompt_choice', 'keep'); return saveNow('keep', { ifRev: rev }); }
            return null;
          }).catch(function () { promptPending = false; });
          return null;
        case 'award_prompt':
          markShown('award:' + rev);
          sendEvent('restore', 'prompt');
          promptPending = true;
          ui.prompt({
            title: COPY.promptTitle,
            body: COPY.awardBody,
            buttons: [{ id: 'load', label: COPY.awardLoad, primary: true }, { id: 'keep', label: COPY.keep }]
          }).then(function (choice) {
            promptPending = false;
            if (choice === 'load') {
              sendEvent('prompt_choice', 'load');
              return dbmPost('/release-award', { expected_customer: identity, cart_token: cart.token || '' })
                .then(function (r) { return r && r.status === 200 ? readBody(r) : null; })
                .catch(function () { return null; })
                .then(function (body) {
                  if (!body || body.awardClaimElsewhere !== false) { ui.notice({ title: COPY.awardFailed }); return null; }
                  return restoreSaved(resp, { replaceOwn: own.length > 0 });
                });
            }
            if (choice === 'keep') { sendEvent('prompt_choice', 'keep'); return saveNow('keep', { ifRev: rev }); }
            return null;
          }).catch(function () { promptPending = false; });
          return null;
        case 'ordered':
          markShown('ordered:' + rev);
          ui.prompt({
            title: COPY.orderedTitle,
            body: COPY.orderedBody,
            buttons: [{ id: 'empty', label: COPY.empty }, { id: 'keep', label: COPY.keep, primary: true }]
          }).then(function (choice) {
            if (choice !== 'empty') { return null; }
            return readCart().then(function (fresh) { return fresh ? emptyCart(fresh) : false; })
              .then(function (ok) { if (ok) { announce(null); } });
          }).catch(noop);
          return null;
        case 'stamp_rev': {
          var a = {};
          a[REV_ATTR] = String(rev);
          a[OWNER_ATTR] = resp.owner;
          setSent(bareToken(cart.token), own);
          return stampAttributes(a).then(function () { try { env.local.setItem(K.fullOwner, resp.owner); } catch (e) {} });
        }
        default:
          // R12 — in full mode a cart with her products is hers from now on, so it is emptied when she signs out.
          if (mode === 'on' && own.length && !cartOwner) {
            var o = {};
            o[OWNER_ATTR] = resp.owner;
            return stampAttributes(o).then(function (ok) { if (ok) { try { env.local.setItem(K.fullOwner, resp.owner); } catch (e) {} } });
          }
          return null;
      }
    }

    function loadCheck() {
      if (!saveOn || checking || !checkDue()) { return Promise.resolve(); }
      checking = true;
      lastCheckAt = now();
      return readCart().then(function (cart) {
        if (!cart) { return null; }
        return getSaved(cart.token).then(function (resp) {
          markChecked();
          if (!resp || resp.signedIn !== true) { return null; }
          return act(resp, cart, 0);
        });
      }).then(function (v) { checking = false; return v; }, function (e) { checking = false; throw e; });
    }

    // Full mode: the tab became visible again (she comes back from her phone to the laptop). Check again, as a
    // page load would, unless this page is already working on the cart: a restore, an open question, a save
    // waiting or out, a check out. Holds the cart lock for the check, as the pre-claim does at page load.
    function visibleCheck() {
      try {
        if (mode !== 'on' || !identity || disabled || reloading || restoring || promptPending || checking) { return; }
        if (timer || inflight || queuedP) { return; }
        var since = now() - lastCheckAt;
        if (since >= 0 && since < VISIBLE_GAP_MS) { return; }
        ensureHold(2500);
        loadCheck().catch(noop).then(function () { releaseHold(); });
      } catch (e) { releaseHold(); }
    }

    // R12 / S11 — nobody is signed in, and this cart was stamped for the buyer who was: empty it.
    function signedOutCheck() {
      if (!fullFeatureLive(cfg.mode)) { return Promise.resolve(); }
      var fullOwner = getRaw(env.local, K.fullOwner);
      if (!fullOwner) { return Promise.resolve(); }
      return readCart().then(function (cart) {
        if (!cart) { return null; }
        var owner = attr(cart, OWNER_ATTR);
        if (!owner) { remove(env.local, K.fullOwner); return null; }
        if (owner !== fullOwner) { return null; }
        return emptyCart(cart).then(function (ok) {
          if (!ok) { return; }
          remove(env.local, K.fullOwner);
          writeJson(env.local, K.emptied, { owner: owner });
          announce(null);
        });
      });
    }

    // --- save ---
    function scheduleSave(trigger) {
      if (!saveOn || disabled) { return; }
      if (pendingTrigger !== 'change') { pendingTrigger = trigger; }
      if (timer) { env.clearTimeout(timer); }
      timer = env.setTimeout(function () {
        timer = null;
        var t = pendingTrigger;
        pendingTrigger = null;
        saveNow(t, {});
      }, DEBOUNCE_MS);
    }

    function ensureState() {
      var s = state();
      if (s && s.owner) { return Promise.resolve(s); }
      return readCart().then(function (cart) { return cart ? getSaved(cart.token) : null; }).then(function () { return state(); });
    }

    var TRIGGER_RANK = { load: 1, change: 2, keep: 3 };

    // ONE save in flight per page. A save asked for while one is out waits for its answer (so it carries the
    // revision that answer returned) and is coalesced: however many arrive, one more save goes out. A pagehide
    // keepalive save cannot wait: it goes at once, on the revision the in-flight save will make.
    function saveNow(trigger, opts) {
      if (!saveOn || disabled || restoring) { return Promise.resolve(null); }
      opts = opts || {};
      if (opts.keepalive || !inflight) { return runSave(trigger, opts); }
      var ifRev = opts.ifRev !== undefined && opts.ifRev !== null ? opts.ifRev : (queued ? queued.ifRev : null);
      var t = queued && (TRIGGER_RANK[queued.trigger] || 0) > (TRIGGER_RANK[trigger] || 0) ? queued.trigger : trigger;
      queued = { trigger: t, ifRev: ifRev };
      if (!queuedP) {
        queuedP = inflight.then(function () {
          var q = queued;
          queued = null;
          queuedP = null;
          return saveNow(q.trigger, q.ifRev !== null && q.ifRev !== undefined ? { ifRev: q.ifRev } : {});
        });
      }
      return queuedP;
    }

    function runSave(trigger, opts) {
      var p = doSave(trigger, opts);
      if (opts.keepalive) { return p; }
      inflight = p;
      p.then(function () { if (inflight === p) { inflight = null; } });
      return p;
    }

    function doSave(trigger, opts) {
      if (!saveOn || disabled || restoring) { return Promise.resolve(null); }
      opts = opts || {};
      var seq;
      if (opts.ifRev !== undefined && opts.ifRev !== null) {
        var cur = state();
        if (cur) { setState(cur.owner, opts.ifRev); }
        clearPending(); // 'Keep this cart' saves over the revision she was shown, not over a guess
      }
      var cartP = opts.cart ? Promise.resolve(opts.cart) : readCart();
      return Promise.all([ensureState(), cartP]).then(function (got) {
        var s = got[0];
        var cart = got[1];
        if (!s || !cart) { return null; }
        var f = flags();
        var own = ownLines(cart, f);
        var cartOwner = attr(cart, OWNER_ATTR);
        var token = bareToken(cart.token);
        // A save whose answer was never seen still counts as sent: its content is (almost always) on the server.
        var pend = pending();
        var sent = pend ? { id: identity, token: pend.token, hash: pend.hash } : lastSent();
        var gate = saveGate({
          identity: identity, state: s, cartOwner: cartOwner, promptPending: promptPending,
          backoffUntil: Number(getRaw(env.local, K.backoff)) || 0, now: now(), own: own, lastSent: sent,
          token: token, blockedRev: blockedRev(), trigger: trigger
        });
        if (!gate.send) { return null; }
        var claim = Promise.resolve();
        if (!cartOwner && !opts.keepalive) {
          var a = {};
          a[OWNER_ATTR] = s.owner;
          claim = stampAttributes(a).then(function (ok) {
            if (ok && mode === 'on') { try { env.local.setItem(K.fullOwner, s.owner); } catch (e) {} }
          });
        }
        return claim.then(function () {
          // The revision is read NOW, not when this save was asked for: the newest answer, or the pending guess.
          var p = pending();
          var known = p ? Number(p.rev) || 0 : Number((state() || s).rev) || 0;
          seq = nextSeq();
          setPending(token, own, known + 1, seq);
          var body = {
            expected_customer: identity,
            device_id: deviceId(),
            device_seq: seq,
            if_rev: known > 0 ? known : null,
            source_cart_token: token,
            lines: own
          };
          var referral = referralFor(deviceReferral(), cfg.selfRef, now());
          if (referral) { body.referral = referral; }
          return dbmPost('', body, !!opts.keepalive);
        }).then(function (r) {
          var status = r ? r.status : 0;
          if (status === 200) {
            return readBody(r).then(function (data) {
              if (data && Number(data.rev) > 0) { setState(s.owner, data.rev); }
              clearPending(seq);
              setSent(token, own);
              remove(env.local, K.blocked);
              remove(env.local, K.emptied);
            });
          }
          clearPending(seq);
          if (status === 409) {
            return readBody(r).then(function (data) { return onConflict(data, trigger, cart); });
          }
          if (status === 429) {
            var ra = r.headers && r.headers.get ? Number(r.headers.get('Retry-After')) : NaN;
            var wait = ra > 0 ? ra * 1000 : 10 * 60 * 1000;
            try { env.local.setItem(K.backoff, String(now() + wait)); } catch (e) {}
            return null;
          }
          // 204 no-op (identity mismatch, view-as, kill switch) and every refusal: stop for this page.
          disabled = true;
          return null;
        });
      }).catch(function () {
        if (seq !== undefined) { clearPending(seq); }
        return null;
      });
    }

    // 409 — someone else saved first (R8), this device is behind, or this cart was already ordered (R14).
    // The answer carries DBM's current revision. stale_sequence is this device's own newer save: adopt the
    // revision, nothing to resend. stale_revision outside full mode: adopt it and retry once, no GET (shadow
    // never prompts). Full mode still GETs: R7/R8 need the saved lines and the device that saved them, and it
    // acts on them like a load (restore / prompt / award / ordered); only otherwise does it retry.
    function onConflict(data, trigger, cart) {
      var reason = data && data.reason;
      var hasRev = !!data && typeof data.rev === 'number' && data.rev >= 0;
      var cur = state();
      if (hasRev && cur && reason === 'stale_sequence') { setState(cur.owner, data.rev); return Promise.resolve(null); }
      if (hasRev && cur && reason === 'stale_revision' && mode !== 'on') {
        setState(cur.owner, data.rev);
        return trigger !== 'retry' ? doSave('retry', { cart: cart }) : Promise.resolve(null);
      }
      return getSaved(cart.token).then(function (resp) {
        if (!resp || resp.signedIn !== true) { return null; }
        if (reason === 'stale_revision') {
          if (mode === 'on') {
            var f = flags();
            var d = decideLoad({
              mode: mode, resp: resp, own: ownLines(cart, f), cartOwner: attr(cart, OWNER_ATTR),
              cartRev: attr(cart, REV_ATTR), deviceId: deviceId(), shown: shown, emptiedOwner: null
            });
            // An empty cart here (restore) LOADS the other device's cart: a retry would save this empty cart
            // over it (annique-issues#413).
            if (d.action === 'restore' || d.action === 'prompt' || d.action === 'award_prompt' || d.action === 'ordered') {
              return act(resp, cart, 0);
            }
          }
          // The change saved last replaces the saved cart: try once more on the new revision.
          if (trigger !== 'retry') { return doSave('retry', { cart: cart }); }
          return null;
        }
        if (reason === 'cart_ordered' && mode === 'on') { return act(resp, cart, 0); }
        return null;
      });
    }

    // The ONE save choke point: every fetch write to /cart/* lands here, whoever made it.
    function installFetchWrapper() {
      var target = env.win;
      var original = target.fetch;
      if (typeof original !== 'function' || original.__dbmCartSync) { return; }
      var wrapped = function (input, init) {
        var p = original.apply(this, arguments);
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
          if (isCartWrite(url, method)) {
            var changeLike = /\/cart\/(change|update)(\.js)?(\?|$)/.test(String(url).split('#')[0]);
            p.then(function (r) {
              if (!r || !r.ok) { return; }
              latestFresh = false;
              if (changeLike && typeof r.clone === 'function') {
                r.clone().json().then(function (cart) {
                  if (cart && Array.isArray(cart.items)) { latestCart = cart; latestFresh = true; }
                }).catch(noop);
              }
              scheduleSave('change');
            }, noop);
          }
        } catch (e) { /* the buyer's request is never touched */ }
        return p;
      };
      wrapped.__dbmCartSync = true;
      target.fetch = wrapped;
    }

    // Leaving the page with a save pending: send it now, with keepalive so the request outlives the page.
    function flushOnLeave() {
      if (!timer) { return; }
      env.clearTimeout(timer);
      timer = null;
      var t = pendingTrigger || 'change';
      pendingTrigger = null;
      saveNow(t, { keepalive: true, cart: latestFresh ? latestCart : null });
    }

    function start() {
      if (cfg.mode === 'off' || mode === 'off') { releaseHold(); return Promise.resolve(); }
      try {
        if (saveOn) {
          installFetchWrapper();
          env.onLeave(flushOnLeave);
        }
        if (mode === 'on' && identity && env.onVisible) { env.onVisible(visibleCheck); }
        var stashed = readJson(env.session, K.notice);
        if (stashed) { remove(env.session, K.notice); ui.notice(stashed); }
      } catch (e) { releaseHold(); return Promise.resolve(); }
      var p = Promise.resolve().then(function () {
        return identity ? loadCheck() : signedOutCheck();
      });
      return p.catch(noop).then(function () {
        releaseHold();
        if (saveOn) { scheduleSave('load'); }
      });
    }

    return {
      start: start, saveNow: saveNow, scheduleSave: scheduleSave, flushOnLeave: flushOnLeave,
      visibleCheck: visibleCheck, decideLoad: decideLoad, mode: mode,
      _debug: function () { return { restoring: restoring, disabled: disabled, promptPending: promptPending, timer: !!timer }; }
    };
  }

  // ---------------------------------------------------------------------------------------------------
  // The page's UI: one notice card at a time. Text is always set as text.
  // ---------------------------------------------------------------------------------------------------
  function domUi(doc) {
    var STYLE_ID = 'dbm-cart-sync-style';
    var current = null;

    function ensureStyle() {
      if (doc.getElementById(STYLE_ID)) { return; }
      var s = doc.createElement('style');
      s.id = STYLE_ID;
      s.textContent = [
        '.dbm-cs-card{position:fixed;z-index:1000;left:16px;right:16px;bottom:16px;max-width:460px;margin:0 auto;',
        'padding:16px;border-radius:12px;background:rgb(var(--color-background,255 255 255));',
        'color:rgb(var(--color-foreground,26 26 26));border:1px solid rgb(var(--color-foreground,26 26 26)/.15);',
        'box-shadow:0 8px 24px rgb(0 0 0/.18);font-size:15px;line-height:1.4}',
        '.dbm-cs-card__title{margin:0 0 6px;font-weight:600}',
        '.dbm-cs-card__body{margin:0 0 12px}',
        '.dbm-cs-card__list{margin:0 0 8px;padding-left:20px}',
        '.dbm-cs-card__actions{display:flex;flex-wrap:wrap;gap:8px}',
        '.dbm-cs-card__actions .button{flex:1 1 auto}'
      ].join('');
      (doc.head || doc.documentElement).appendChild(s);
    }

    function el(tag, cls, text) {
      var e = doc.createElement(tag);
      if (cls) { e.className = cls; }
      if (text) { e.textContent = text; }
      return e;
    }

    function close() { if (current && current.parentNode) { current.parentNode.removeChild(current); } current = null; }

    function card(role, title) {
      ensureStyle();
      close();
      var c = el('div', 'dbm-cs-card');
      c.setAttribute('role', role);
      var id = 'dbm-cs-title-' + String(Math.random()).slice(2, 8);
      var h = el('p', 'dbm-cs-card__title', title);
      h.id = id;
      c.setAttribute('aria-labelledby', id);
      c.appendChild(h);
      return c;
    }

    function prompt(opts) {
      return new Promise(function (resolve) {
        var c = card('dialog', opts.title);
        if (opts.body) { c.appendChild(el('p', 'dbm-cs-card__body', opts.body)); }
        var actions = el('div', 'dbm-cs-card__actions');
        opts.buttons.forEach(function (b) {
          var btn = el('button', 'button button--sm' + (b.primary ? '' : ' button--outline'), b.label);
          btn.type = 'button';
          btn.addEventListener('click', function () { close(); resolve(b.id); });
          actions.appendChild(btn);
        });
        c.appendChild(actions);
        doc.body.appendChild(c);
        current = c;
      });
    }

    function notice(opts) {
      var c = card('status', opts.title);
      c.setAttribute('aria-live', 'polite');
      if (opts.body) { c.appendChild(el('p', 'dbm-cs-card__body', opts.body)); }
      if (opts.items && opts.items.length) {
        var ul = el('ul', 'dbm-cs-card__list');
        opts.items.forEach(function (t) { ul.appendChild(el('li', '', t)); });
        c.appendChild(ul);
      }
      if (opts.foot) { c.appendChild(el('p', 'dbm-cs-card__body', opts.foot)); }
      var actions = el('div', 'dbm-cs-card__actions');
      var ok = el('button', 'button button--sm', COPY.ok);
      ok.type = 'button';
      ok.addEventListener('click', close);
      actions.appendChild(ok);
      c.appendChild(actions);
      doc.body.appendChild(c);
      current = c;
    }

    return { prompt: prompt, notice: notice };
  }

  if (win.__dbmCartSyncTest) {
    win.DBMCartSyncInternals = { core: core, create: create, domUi: domUi };
    return;
  }

  // ---------------------------------------------------------------------------------------------------
  // Boot. Off means off: nothing below runs, and no request is made.
  // ---------------------------------------------------------------------------------------------------
  try {
    if (win.__dbmCartSyncInit) { return; }
    win.__dbmCartSyncInit = true;
    var cfg = win.__dbmCartSync || {};
    var pre = win.__dbmCartSyncHold || null;
    if (effectiveMode(cfg.mode, cfg.pilot) === 'off') { if (pre) { pre.release(); } return; }
    var doc = win.document;
    var selfRef = '';
    try { selfRef = win.__dbmSelfRef ? String(win.__dbmSelfRef) : ''; } catch (e) {}
    cfg.selfRef = selfRef;
    var instance = create({
      win: win,
      cfg: cfg,
      identity: win.__dbmCartIdentity === null || win.__dbmCartIdentity === undefined ? '' : String(win.__dbmCartIdentity),
      fetch: win.fetch.bind(win),
      lock: win.DBMCartLock,
      hold: pre,
      local: win.localStorage,
      session: win.sessionStorage,
      now: function () { return Date.now(); },
      setTimeout: function (fn, ms) { return win.setTimeout(fn, ms); },
      clearTimeout: function (t) { win.clearTimeout(t); },
      delay: function (ms) { return new Promise(function (r) { win.setTimeout(r, ms); }); },
      AbortController: win.AbortController,
      randomBytes: function (n) {
        var a = new Uint8Array(n);
        if (win.crypto && win.crypto.getRandomValues) { win.crypto.getRandomValues(a); }
        else { for (var i = 0; i < n; i++) { a[i] = Math.floor(Math.random() * 256); } }
        return a;
      },
      root: (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/',
      getCookie: function () { return doc.cookie; },
      setCookie: function (c) { doc.cookie = c; },
      referralValue: function () { return win.DBMReferral ? win.DBMReferral.value : ''; },
      setReferralValue: function (v) { if (win.DBMReferral) { win.DBMReferral.value = v; } },
      giftMap: function () { return win.dbmGwp && win.dbmGwp.giftMap; },
      lineMarkers: function () {
        var out = [];
        var nodes = doc.querySelectorAll('[data-dbm-offer-line]');
        for (var i = 0; i < nodes.length; i++) {
          var n = nodes[i];
          var offer = false;
          try { var blob = JSON.parse(n.getAttribute('data-fn-pricing') || 'null'); offer = !!(blob && blob.offer); } catch (e) {}
          out.push({ key: n.getAttribute('data-key'), gift: n.getAttribute('data-gift') === '1', offer: offer });
        }
        return out;
      },
      onCartPage: function () {
        return !!(win.themeVariables && win.themeVariables.settings && win.themeVariables.settings.pageType === 'cart');
      },
      reload: function () { win.location.reload(); },
      dispatch: function (name) {
        try { doc.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true })); } catch (e) {}
      },
      onLeave: function (fn) {
        win.addEventListener('pagehide', fn);
        doc.addEventListener('visibilitychange', function () { if (doc.visibilityState === 'hidden') { fn(); } });
      },
      // Back on this tab: visible again, or shown from the back/forward cache (where no script re-runs).
      onVisible: function (fn) {
        doc.addEventListener('visibilitychange', function () { if (doc.visibilityState === 'visible') { fn(); } });
        win.addEventListener('pageshow', function (e) { if (e && e.persisted) { fn(); } });
      },
      ui: domUi(doc)
    });
    var go = function () { instance.start().catch(function () {}); };
    if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', go); } else { go(); }
  } catch (e) {
    // S12 — fail closed: free the cart for the other scripts and do nothing.
    try { if (win.__dbmCartSyncHold) { win.__dbmCartSyncHold.release(); } } catch (e2) {}
  }
})(window);
